import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IndexLayoutSearch } from "@cohesive/ui-components";
import { CheckCircle, Filter, Search, X } from "lucide-react";
import React from "react";

interface MyConnectionsHeaderProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  showConnected: boolean;
  setShowConnected: (show: boolean) => void;
  allCategories: string[];
  handleClearFilters: () => void;
  getCategoryDisplayName: (category: string) => string;
}

export const MyConnectionsHeader: React.FC<MyConnectionsHeaderProps> = ({
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  showConnected,
  setShowConnected,
  allCategories,
  handleClearFilters,
  getCategoryDisplayName,
}) => {
  return (
    <>
      <IndexLayoutSearch>
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-end">
          <div className="w-full flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Search integrations by name, description, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-background/80 focus:border-primary/50 focus:ring-rust/10 transition-all duration-200"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-muted/50"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="!w-3 !h-3" />
                </Button>
              )}
            </div>
          </div>
          <div className="w-full lg:w-48">
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="h-12 bg-background/80 transition-colors">
                <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {allCategories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {getCategoryDisplayName(category)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {(searchTerm || selectedCategory !== "all" || !showConnected) && (
          <div className="flex items-center flex-wrap gap-2 mt-6 pt-4 border-t">
            {searchTerm && (
              <Badge className="px-1 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20">
                <Search className="w-3 h-3 mr-1" />"{searchTerm}"
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-1 h-4 w-4 p-0 hover:bg-primary/20"
                  onClick={() => setSearchTerm("")}
                >
                  <X className="!w-3 !h-3" />
                </Button>
              </Badge>
            )}
            {selectedCategory !== "all" && (
              <Badge className="px-1 bg-spectrum-blue-light/15 hover:bg-spectrum-blue-light/30 text-teal border-spectrum-blue-light dark:bg-spectrum-blue-dark/30 hover:dark:bg-spectrum-blue-dark/30 dark:text-spectrum-blue-light dark:border-spectrum-blue-dark">
                <Filter className="w-3 h-3 mr-1" />
                {getCategoryDisplayName(selectedCategory)}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-1 h-4 w-4 p-0 hover:bg-blue-200 dark:hover:bg-blue-800"
                  onClick={() => setSelectedCategory("all")}
                >
                  <X className="!w-3 !h-3" />
                </Button>
              </Badge>
            )}
            {!showConnected && (
              <Badge className="px-1 bg-lc-warning/10 hover:bg-spectrum-orange-light/30 text-lc-warning border-lc-warning/30 dark:bg-spectrum-orange-dark/30 hover:dark:bg-spectrum-orange-dark/30 dark:text-lc-warning dark:border-lc-warning/40">
                <CheckCircle className="w-3 h-3 mr-1" />
                Connected Hidden
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-1 h-4 w-4 p-0 hover:bg-orange-200 dark:hover:bg-orange-800"
                  onClick={() => setShowConnected(true)}
                >
                  <X className="!w-3 !h-3" />
                </Button>
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="rounded-fulltext-xs text-muted-foreground hover:text-foreground"
            >
              Clear all
            </Button>
          </div>
        )}
      </IndexLayoutSearch>
    </>
  );
};

export default MyConnectionsHeader;
